import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ESCALATION_FACTOR,
  SUBJECT_LABELS,
  WAR_ROOM_PRIORITY_DEFS,
  WAR_ROOM_PRIORITY_MAP,
  type SignalSubject,
  type WarRoomPriority,
  type WarRoomRuleKey,
} from '@goyone/shared';
import { api } from '../../lib/api';
import { Icon, type IconName } from '../../components/Icon';
import { ErrorState, PageHead } from '../../components/ui';
import { useToast } from '../../components/Toast';

interface EffectiveRule {
  key: WarRoomRuleKey;
  label: string;
  description: string;
  subject: SignalSubject;
  enabled: boolean;
  threshold: number;
  thresholdUnit: 'DAYS' | 'MONEY';
  thresholdLabel: string;
  priority: WarRoomPriority;
  customised: boolean;
}

/** كم إشارةً ترصدها كلُّ قاعدةٍ الآن — لضبطِ الحدِّ على واقعٍ لا على تخمين. */
interface BoardPeek {
  groups: Array<{ rule: WarRoomRuleKey; total: number }>;
}

/** أيقونةُ الوحدةِ التي تخصُّها القاعدة. */
const SUBJECT_ICONS: Record<SignalSubject, IconName> = {
  LEAD: 'user',
  ACCOUNT: 'building',
  DEAL: 'briefcase',
  QUOTATION: 'note',
  CONTRACT: 'book',
  INVOICE: 'receipt',
  EVENT: 'calendar',
  ACTIVITY: 'target',
};

/**
 * قواعدُ الرصد — في الإعدادات لا في غرفة العمليات.
 *
 * الغرفةُ تُفتح كلَّ صباحٍ لتُفرَغ، والقواعدُ تُضبط مرّةً حين تُعرف دورةُ
 * البيع ثمّ تُنسى. زرُّ ضبطٍ بين أزرار العمل اليوميّ يُضغط سهوًا ويُحدَث
 * به الحدُّ في منتصف جلسة. وموضعُ ما يُضبط مرّةً هو الإعدادات.
 */
export function WarRoomRules() {
  const queryClient = useQueryClient();
  const toast = useToast();

  const rules = useQuery({
    queryKey: ['war-room', 'rules'],
    queryFn: () => api<EffectiveRule[]>('/crm/war-room/rules'),
  });

  /* لقطةٌ خفيفة: إشارةٌ واحدةٌ لكلِّ قاعدةٍ تكفي لمعرفةِ العدد الكلّيّ. */
  const peek = useQuery({
    queryKey: ['war-room', 'peek'],
    queryFn: () => api<BoardPeek>('/crm/war-room?perRule=1'),
  });
  const counts = new Map((peek.data?.groups ?? []).map((g) => [g.rule, g.total]));

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['war-room'] });
  };

  const save = useMutation({
    mutationFn: ({ key, body }: { key: string; body: Record<string, unknown> }) =>
      api(`/crm/war-room/rules/${key}`, { method: 'PATCH', body }),
    onSuccess: invalidate,
    onError: (e) => toast(e instanceof Error ? e.message : 'تعذّر الحفظ'),
  });

  const reset = useMutation({
    mutationFn: (key: string) => api(`/crm/war-room/rules/${key}`, { method: 'DELETE' }),
    onSuccess: () => {
      invalidate();
      toast('أُعيدت القاعدة إلى قيمها الافتراضية', 'success');
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'تعذّرت الاستعادة'),
  });

  const all = rules.data ?? [];
  const on = all.filter((r) => r.enabled).length;
  const watched = all
    .filter((r) => r.enabled)
    .reduce((s, r) => s + (counts.get(r.key) ?? 0), 0);

  /* تُجمع القواعدُ بوحدتها: من يضبط العملاء المحتملين يضبطهم معًا. */
  const subjects = [...new Set(all.map((r) => r.subject))];

  return (
    <div className="page">
      <div className="page-sticky">
        <PageHead
          title="قواعد الرصد"
          subtitle="ما تعدّه غرفةُ العمليات مستحقًّا للتدخّل، وبأيّ حدّ"
          actions={
            <Link to="/crm/war-room" className="btn btn-primary">
              <Icon name="zap" size={15} />
              غرفة العمليات
            </Link>
          }
        />

        {/* لافتةٌ موجزة: كم قاعدةً تعمل، وكم إشارةً ترصدها الآن. */}
        <div className="wrr-hero">
          <span className="wrr-hero-ic">
            <Icon name="zap" size={22} />
          </span>
          <div className="wrr-hero-txt">
            <h2>الرصدُ يعمل بلا أن يُسأل</h2>
            <p>
              القاعدةُ الموقوفة لا تُرصد إشاراتها. والحدُّ هو ما يفصل الوضعَ الطبيعيَّ عمّا يستحقّ
              التدخّل — ويختلف بين شركةٍ وأخرى بحسب دورة بيعها. والتغييرُ يسري على الغرفة فورًا.
            </p>
          </div>
          <div className="wrr-hero-stats">
            <div className="wrr-stat">
              <strong className="num">
                {on}
                <small>/{all.length}</small>
              </strong>
              <span>قاعدة مفعَّلة</span>
            </div>
            <div className="wrr-stat is-warn">
              <strong className="num">{watched}</strong>
              <span>إشارة الآن</span>
            </div>
          </div>
        </div>
      </div>

      {rules.isError && <ErrorState error={rules.error} onRetry={() => void rules.refetch()} />}
      {rules.isPending && <div className="skeleton" style={{ height: 300 }} />}

      {subjects.map((subject) => (
        <section className="wrr-group" key={subject}>
          <h3 className="wrr-group-title">
            <span className="wrr-group-ic">
              <Icon name={SUBJECT_ICONS[subject]} size={15} />
            </span>
            {SUBJECT_LABELS[subject]}
          </h3>

          <div className="wrr-rules">
            {all
              .filter((r) => r.subject === subject)
              .map((rule) => {
                const live = counts.get(rule.key) ?? 0;
                return (
                  <div className={`wrr-rule${rule.enabled ? '' : ' is-off'}`} key={rule.key}>
                    <div className="wrr-rule-main">
                      <div className="wrr-rule-top">
                        <label className="switch">
                          <input
                            type="checkbox"
                            checked={rule.enabled}
                            aria-label={`تفعيل ${rule.label}`}
                            onChange={(e) =>
                              save.mutate({ key: rule.key, body: { enabled: e.target.checked } })
                            }
                          />
                          <span className="switch-track">
                            <span className="switch-thumb" />
                          </span>
                        </label>
                        <strong className="wrr-rule-label">{rule.label}</strong>
                        <span className={`badge b-${WAR_ROOM_PRIORITY_MAP[rule.priority].kind}`}>
                          {WAR_ROOM_PRIORITY_MAP[rule.priority].label}
                        </span>
                        {rule.enabled && (
                          <span className={`wrr-live${live > 0 ? ' is-hot' : ''}`}>
                            <span className="num">{live}</span> الآن
                          </span>
                        )}
                        {rule.customised && <span className="wrr-tag">مضبوطة</span>}
                      </div>
                      <p className="wrr-rule-desc">{rule.description}</p>
                    </div>

                    <div className="wrr-rule-controls">
                      <label className="wrr-ctl">
                        <span className="wrr-ctl-label">{rule.thresholdLabel}</span>
                        <div className="wrr-threshold">
                          <input
                            className="input num"
                            type="number"
                            min="0"
                            defaultValue={rule.threshold}
                            disabled={!rule.enabled}
                            aria-label={`حد ${rule.label}`}
                            onBlur={(e) => {
                              const v = Number(e.target.value);
                              if (Number.isFinite(v) && v !== rule.threshold) {
                                save.mutate({ key: rule.key, body: { threshold: v } });
                              }
                            }}
                          />
                          <span className="wrr-unit">
                            {rule.thresholdUnit === 'DAYS' ? 'يومًا' : 'ج.م'}
                          </span>
                        </div>
                      </label>

                      <label className="wrr-ctl">
                        <span className="wrr-ctl-label">الأولوية</span>
                        <select
                          className="select"
                          value={rule.priority}
                          disabled={!rule.enabled}
                          aria-label={`أولوية ${rule.label}`}
                          onChange={(e) =>
                            save.mutate({ key: rule.key, body: { priority: e.target.value } })
                          }
                        >
                          {WAR_ROOM_PRIORITY_DEFS.map((p) => (
                            <option key={p.key} value={p.key}>
                              {p.label}
                            </option>
                          ))}
                        </select>
                      </label>

                      <button
                        className="btn btn-sm btn-icon"
                        onClick={() => reset.mutate(rule.key)}
                        disabled={!rule.customised || reset.isPending}
                        title="إعادة القاعدة إلى قيمها الافتراضية"
                        aria-label={`استعادة ${rule.label}`}
                      >
                        <Icon name="refresh" size={14} />
                      </button>
                    </div>
                  </div>
                );
              })}
          </div>
        </section>
      ))}

      <p className="tiny mute mt-4">
        التصعيدُ تلقائيّ: إشارةٌ تجاوزت حدَّها {ESCALATION_FACTOR} أضعافٍ تُرفع أولويّتُها درجةً
        وتُعلَّم «مُصعَّدة» في الغرفة — فلا يضيع ما طال تأخّره بين ما تأخّر للتوّ.
      </p>
    </div>
  );
}
